/* nb-chat.js — sticky add-to-cart detection for the floating WhatsApp trigger.
 *
 * Until 2026-08-07 this file also bridged to the Zaapi live-chat iframe and
 * intercepted every WhatsApp link on the site, rerouting it to that chat. Both
 * are gone: the trigger is now an ordinary link to WhatsApp, so intercepting
 * WhatsApp links would break the very thing it points at.
 *
 * What remains lifts the bubble clear of the sticky add-to-cart bar, and only
 * while that bar is genuinely on screen.
 *
 * Two things make this less trivial than it looks:
 *   1. Presence is not visibility. Minimog and our own bars sit in the DOM from
 *      page load and slide in with transform: translateY(100%). A CSS :has()
 *      test matched from the first paint and parked the bubble a third of the
 *      way up an unscrolled product page.
 *   2. The bars are children, not the sections. .nuvie-sticky-*-section are
 *      zero-height <section> wrappers; .nuvie-sdb and .nuvie-smb are the fixed
 *      bars inside them.
 *
 * Detection uses IntersectionObserver rather than comparing rects to
 * window.innerHeight. It understands transforms natively, needs no scroll
 * handler, and cannot silently fail open when a viewport measurement is
 * unavailable. The measured height is published as --nb-atc-height so the CSS
 * can sit the bubble just above whatever bar is showing.
 */
(function () {
  'use strict';

  var SELECTOR = '.nuvie-sdb, .nuvie-smb, .m-sticky-addtocart, ' +
                 '.nb-sticky-atc, .nb-sticky-atc-bar-wrapper, .nb-satc';
  var MIN_BAR_HEIGHT = 20;

  var observed = new WeakSet();
  var visible = new Map();   // bar element -> on-screen height in px
  var io = null;
  var lastApplied = -1;

  function apply() {
    var tallest = 0;
    visible.forEach(function (h) { if (h > tallest) tallest = h; });
    tallest = Math.round(tallest);
    if (tallest === lastApplied) return;
    lastApplied = tallest;

    if (tallest > 0) {
      document.documentElement.style.setProperty('--nb-atc-height', tallest + 'px');
      document.body.classList.add('nb-has-sticky-atc');
    } else {
      document.body.classList.remove('nb-has-sticky-atc');
      document.documentElement.style.removeProperty('--nb-atc-height');
    }
  }

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      // intersectionRect is the part actually on screen, so a bar sliding in
      // reports a growing height and the bubble tracks it.
      var h = entry.isIntersecting ? entry.intersectionRect.height : 0;
      if (h >= MIN_BAR_HEIGHT) {
        visible.set(entry.target, h);
      } else {
        visible.delete(entry.target);
      }
    });
    apply();
  }

  function observeAll() {
    var bars = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < bars.length; i++) {
      if (observed.has(bars[i])) continue;
      observed.add(bars[i]);
      io.observe(bars[i]);
    }
  }

  function init() {
    if (!('IntersectionObserver' in window)) return;  // fail closed: bubble stays put

    // A staircase of thresholds so a bar sliding up reports intermediate
    // heights instead of snapping from 0 to full.
    var thresholds = [];
    for (var i = 0; i <= 20; i++) thresholds.push(i / 20);

    io = new IntersectionObserver(onIntersect, { threshold: thresholds });
    observeAll();

    // Bars can be injected late, swapped on variant change, or added by apps.
    if (window.MutationObserver) {
      new MutationObserver(observeAll).observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
